import moment from 'moment';
import { postEvent, getVenuesV3 } from '../../../../../ui-kitchen/src/api/api';
import { parameterize } from '../../../../../ui-kitchen/src/utils';
import eventTypes from '../../../../../ui-kitchen/src/constants/eventTypes';
import { setEventNotify, deleteEventNotify } from '../../../../../ui-kitchen/src/containers/EventBookingContainer/EventBookingAPI';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';

const CONTEXT = 'Resy Experiences page';
const CATEGORY = 'Resy Experiences Interaction';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm:ss';
export const DATE_TIME_FORMAT_EVENT = 'YYYY-MM-DD HH:mm:ss';
export const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm:ss';
const DEFAULT_TIMEZONE_NAME = 'America/New_York';

export const GDA_ELIGIBLE = 'GDAEligible';
export const GDA_MEMBER = 'GDAMember';
export const GDA_COOKIE_NAME = 'GDA';

export const ANALYTICS_PARAMS_EXPERIENTIAL = {
  context: CONTEXT,
  category: CATEGORY,
};

export const EVENT_SOURCE = {
  HARDCODED: 'hardcoded',
  RESY_API: 'resy',
  VENUE_API: 'venue',
};

const ERROR_SOURCE = {
  JSON: 'JSON',
  API: 'API',
};

const ERROR_TYPE = {
  MANDATORY: 'Mandatory',
  VALIDATION: 'Validation',
  APIERROR: 'API Error',
};

const { SEATED, GENERAL_ADMISSION } = eventTypes;
const eventTypeMap = {
  1: GENERAL_ADMISSION,
  2: SEATED,
};

export const NEW_EVENTS = 'NEW_EVENTS';
export const MAIN_EVENTS = 'MAIN_EVENTS';
export const PAST_EVENTS = 'PAST_EVENTS';
export const ALL_EVENTS = 'All Events';
export const ALL_MONTHS = 'All Months';
export const ALL_AVAILABLE = 'All Available';
export const COMING_SOON = 'Coming Soon';
export const CITY_FILTER = 'CITY';
export const FILTER_EVENTS = 'FILTER_EVENTS';

const getCurrentMomentDate = (timezone, timezoneMapping) => {
  if (timezone) moment.tz.setDefault(timezoneMapping[timezone] || timezone);
  else moment.tz.setDefault(DEFAULT_TIMEZONE_NAME); // if timezone is undefined
  const nowEST = moment();
  const currentDate = nowEST.format(DATE_TIME_FORMAT);
  return moment(currentDate, DATE_TIME_FORMAT);
};

const getCurrentDate = () => {
  moment.tz.setDefault(DEFAULT_TIMEZONE_NAME);
  const nowEST = moment();
  const currentDate = nowEST.format(DATE_FORMAT);
  return currentDate;
};

const handleTaggingWithProps = (eventName, Analytics, extraParams) => {
  const params = {
    context: CONTEXT,
    category: CATEGORY,
    ...(extraParams || {}),
  };
  Analytics.event(eventName, params);
};

const consoleError = (message, fieldName, objectValue) => {
  const updatedMessage = message.replace('fieldName', fieldName);
  Logger.error(updatedMessage, objectValue || '');
};

const validateJSONHardcodedEvents = (hardcodedEvents, showAll, errorMessages) => {
  const errorMessage = '[AmexExperiential] fieldName is a Mandatory field in this Hardcoded Event -';

  const requiredKeys = [
    'event_name',
    'event_date_time',
    'event_end_date_time',
    'responsive_images',
    'display_start_date_time',
    'display_end_date_time',
    'location',
  ];

  const validatedHardcodedEvents = [];

  hardcodedEvents.forEach((event) => {
    let noErrors = true;
    const fieldNames = [];
    requiredKeys.forEach((item) => {
      const val = event[item];
      if ((typeof event[item] === 'string' && val === '') || val === null || !val) {
        noErrors = false;
        fieldNames.push(item);
        consoleError(errorMessage, item, event);
      } else if (typeof event[item] === 'object' && Object.keys(val).length === 0) {
        noErrors = false;
        fieldNames.push(item);
        consoleError(errorMessage, item, event);
      }
    });
    if (!noErrors && showAll) {
      const errObj = {
        error_source: ERROR_SOURCE.JSON,
        error_type: ERROR_TYPE.MANDATORY,
        field_names: fieldNames.toString(),
        error_message: errorMessages?.error_json_message,
      };
      event.error = errObj;
    } else if (noErrors) {
      validatedHardcodedEvents.push(event);
    }
  });

  if (!showAll) return validatedHardcodedEvents;
  return hardcodedEvents;
};

const validateJSONResyEvents = (resyEvents, showAll, errorMessages) => {
  const requiredKeys = [
    'location',
    'event_slug',
    'venue_slug'];

  const errorMessage = '[AmexExperiential] JSON VALIDATION - fieldName is a Mandatory field in this JSON Resy Event -';

  const resyEventsWithoutErrors = [];

  resyEvents.forEach((event) => {
    let noErrors = true;
    const fieldNames = [];
    requiredKeys.forEach((item) => {
      const val = event[item];
      if ((typeof event[item] === 'string' && val === '') || val === null || !val) {
        noErrors = false;
        fieldNames.push(item);
        consoleError(errorMessage, item, event);
      }
    });

    if (!noErrors && showAll) {
      const errObj = {
        error_source: ERROR_SOURCE.JSON,
        error_type: ERROR_TYPE.MANDATORY,
        field_names: fieldNames.toString(),
        error_message: errorMessages?.error_json_message,
      };
      event.error = errObj;
    }

    if (noErrors && !showAll) {
      resyEventsWithoutErrors.push(event);
    }
  });

  if (!showAll) return resyEventsWithoutErrors;
  return resyEvents;
};

const validateJSONVenueEvents = (venueEvents, showAll, errorMessages) => {
  const requiredKeys = [
    'venue_id',
    'event_date_time',
    'event_end_date_time',
  ];

  const venuesWithoutErrors = [];

  venueEvents.forEach((event) => {
    let noErrors = true;
    let validationNoErrors = true;
    const fieldNames = [];
    requiredKeys.forEach((item) => {
      const val = event[item];
      if ((typeof event[item] === 'string' && val === '') || val === null || !val) {
        noErrors = false;
        fieldNames.push(item);
        Logger.error(`[AmexExperiential] JSON VALIDATION - ${item} is a Mandatory field in this API Restaurant Event -`, event);
      }
    });

    if (!noErrors && showAll) {
      const errObj = {
        error_source: ERROR_SOURCE.JSON,
        error_type: ERROR_TYPE.MANDATORY,
        field_names: fieldNames.toString(),
        error_message: errorMessages?.error_json_message,
      };
      event.error = errObj;
    }

    let errorMessage = '';
    if (event.event_date_time && event.event_date_time.split(' ').length !== 2) {
      Logger.error('[AmexExperiential] JSON VALIDATION - event_date_time is of invalid format for API Restaurant Event  -', event);
      fieldNames.push('event_date_time');
      validationNoErrors = false;
      errorMessage = `${errorMessages?.error_validation_message} - event_date_time is of invalid format`;
    }

    if (event.event_end_date_time && event.event_end_date_time.split(' ').length !== 2) {
      Logger.error('[AmexExperiential] JSON VALIDATION - event_end_date_time is of invalid format for API Restaurant Event  -', event);
      validationNoErrors = false;
      fieldNames.push('event_end_date_time');
      errorMessage = `${errorMessages?.error_validation_message} - event_end_date_time is of invalid format`;
    }

    if (moment(event.event_end_date_time, DATE_TIME_FORMAT).isBefore(moment(event.event_date_time, DATE_TIME_FORMAT))) {
      Logger.error('[AmexExperiential] JSON VALIDATION - event_end_date_time is before event_date_time for API Restaurant Event  -', event);
      validationNoErrors = false;
      fieldNames.push('event_end_date_time and event_date_time');
      errorMessage = `${errorMessages?.error_validation_message} - event_end_date_time is before event_date_time`;
    }

    if (noErrors && !validationNoErrors && showAll) {
      const errObj = {
        error_source: ERROR_SOURCE.JSON,
        error_type: ERROR_TYPE.VALIDATION,
        field_names: fieldNames.toString(),
        error_message: errorMessage,
      };
      event.error = errObj;
    }

    if (noErrors && validationNoErrors && !showAll) {
      venuesWithoutErrors.push(event);
    }
  });

  if (!showAll) return venuesWithoutErrors;
  return venueEvents;
};

const updateWithCommonFields = (event, ctaLabel, startDateOffset, endDateOffset, timezoneMapping) => {
  const {
    cta_label: eventCtaLabel, display_start_date_time: eventDisplayStartDateTime,
    event_date_time: eventDateTimeValue,
    event_end_date_time: eventEndDateTimeValue,
    display_end_date_time: eventDisplayEndDateTime, timezone,
  } = event;

  if (!eventCtaLabel) {
    event.cta_label = ctaLabel;
  }

  if (timezone) {
    const cityTimezoneName = timezoneMapping[timezone] || timezone;
    moment.tz.setDefault(cityTimezoneName);
  }

  if (!eventDisplayStartDateTime) {
    const eventDateTime = moment(eventDateTimeValue, DATE_TIME_FORMAT);
    const displayStartDateTimeMoment = eventDateTime.subtract(startDateOffset, 'days');
    const displayStartDateTime = displayStartDateTimeMoment.format(DATE_TIME_FORMAT);
    event.display_start_date_time = displayStartDateTime;
  }

  if (!eventDisplayEndDateTime) {
    const eventEndDateTime = moment(eventEndDateTimeValue, DATE_TIME_FORMAT);
    const displayEndDateTimeMoment = eventEndDateTime.add(endDateOffset, 'days');
    const displayEndDateTime = displayEndDateTimeMoment.format(DATE_TIME_FORMAT);
    event.display_end_date_time = displayEndDateTime;
  }
};

const validateAPIVenueEvents = (venueEvents, {
  cta_label: ctaLabel,
  display_start_date_offset: startDateOffset,
  display_end_date_offset: endDateOffset,
}, timezoneMapping, showAll, errorMessages) => {
  if (!venueEvents) return [];

  const finalyEvents = [];
  const requiredKeys = [
    'event_name',
    'responsive_images',
    'location',
    'venue_slug',
  ];

  venueEvents.forEach((event) => {
    if (!event.error && event && Object.keys(event).length !== 0) {
      let isPresent = true;
      requiredKeys.forEach((item) => {
        const val = event[item];
        if ((typeof event[item] === 'string' && val === '') || val === null || !val) {
          Logger.error(`[AmexExperiential] ${item} is a Mandatory field in this API Venue Event  -`, event);
          isPresent = false;
        } else if ((typeof event[item] === 'object' && (val === null || !val))) {
          Logger.error(`[AmexExperiential] ${item} Object is a Mandatory field in this API Venue Event  -`, event);
          isPresent = false;
        }
      });

      if (!isPresent && showAll) {
        const errObj = {
          error_source: ERROR_SOURCE.API,
          error_type: ERROR_TYPE.MANDATORY,
          error_message: errorMessages?.error_api_message,
        };
        event.error = errObj;
        finalyEvents.push(event);
      }

      if (isPresent && !showAll) {
        const {
          cta_label: eventCtaLabel,
        } = event;

        if (!eventCtaLabel) {
          event.cta_label = ctaLabel;
        }
        finalyEvents.push(event);
      } else if (isPresent) {
        updateWithCommonFields(event, ctaLabel, startDateOffset, endDateOffset, timezoneMapping);
        finalyEvents.push(event);
      }
    } else if (showAll) {
      finalyEvents.push(event);
    }
  });

  return finalyEvents;
};

const validateAPIResyEvents = (resyEvents, {
  cta_label: ctaLabel,
  display_start_date_offset: startDateOffset,
  display_end_date_offset: endDateOffset,
}, timezoneMapping, showAll, errorMessages) => {
  const finalyEvents = [];
  const requiredKeys = [
    'event_name',
    'event_date',
    'event_time',
    'image_preview',
    'responsive_images'];

  resyEvents.forEach((event) => {
    if (event && !event.error) {
      let isPresent = true;
      requiredKeys.forEach((item) => {
        const val = event[item];
        if ((typeof event[item] === 'string' && val === '') || val === null || !val) {
          Logger.error(`[AmexExperiential] ${item} is a Mandatory field in this API Resy Event  -`, event);
          isPresent = false;
        } else if ((typeof event[item] === 'object' && (val === null || !val))) {
          Logger.error(`[AmexExperiential] ${item} Object is a Mandatory field in this API Resy Event  -`, event);
          isPresent = false;
        }
      });

      if (!isPresent && showAll) {
        const errObj = {
          error_source: ERROR_SOURCE.API,
          error_type: ERROR_TYPE.MANDATORY,
          error_message: errorMessages?.error_api_message,
        };
        event.error = errObj;
        finalyEvents.push(event);
      } else if (isPresent) {
        updateWithCommonFields(event, ctaLabel, startDateOffset, endDateOffset, timezoneMapping);
        finalyEvents.push(event);
      }
    } else if (showAll) {
      finalyEvents.push(event);
    }
  });

  return finalyEvents;
};

const fetchVenueDetails = async (venueIds) => {
  const params = {
    day: getCurrentDate(),
    party_size: 2,
    venue_ids: venueIds,
    resy_token: undefined,
    lat: 0,
    long: 0,
  };

  let venueResponseList = [];

  try {
    const venueResponse = await getVenuesV3(params);
    venueResponseList = venueResponse?.results?.venues;
  } catch (error) {
    Logger.error(`ERROR [AmexExperiential] fetchVenueDetails API for Venue IDs-- ${venueIds} --`, error?.data?.data);
  }
  return venueResponseList;
};

const fetchEventDetails = async (eventsDataItem, selectedpartySize) => {
  const { location, venue_slug: venueSlug, event_slug: eventSlug } = eventsDataItem;
  try {
    const params = {
      event_url_slug: eventSlug,
      location,
      num_seats: selectedpartySize,
      venue_url_slug: venueSlug,
    };

    const result = await postEvent(params);

    return result?.event;
  } catch (error) {
    Logger.error(`ERROR [AmexExperiential] fetchEventDetails API -- ${sanitizeString(JSON.stringify(eventsDataItem))} -- `, error?.data?.data);
  }
  return null;
};

const getEventCityTimeZone = (location, locationBySlug) => {
  const cityTimezone = locationBySlug[location]?.time_zone || '';
  return cityTimezone;
};

const getEventDateTimeInEST = (location, locationBySlug, dateTime, dateFormat, timezoneMapping) => {
  const cityTimezone = getEventCityTimeZone(location, locationBySlug);
  const cityTimezomeName = timezoneMapping[cityTimezone] || cityTimezone;
  moment.tz.setDefault(cityTimezomeName);

  const eventDateTimeInCityTimeZone = moment(dateTime, dateFormat);
  const eventDateTimeESTFormatted = eventDateTimeInCityTimeZone.tz(DEFAULT_TIMEZONE_NAME).format(DATE_TIME_FORMAT);
  return eventDateTimeESTFormatted;
};

const parseValidVenueEvents = (eventsData, eventsConfig, timezoneMapping) => {
  const parsedEventsData = [];
  const {
    display_start_date_offset: startDateOffset,
    display_end_date_offset: endDateOffset,
  } = eventsConfig;

  eventsData.forEach((element) => {
    if (element.error) parsedEventsData.push(element);
    else {
      const {
        display_start_date_time: eventDisplayStartDateTime,
        display_end_date_time: eventDisplayEndDateTime,
        timezone,
        event_date_time: eventDateTimeValue,
        event_end_date_time: eventEndDateTimeValue,
        display_as_past_event_id: displayAsPastEventId,
      } = element;
      const parsedCurrentDate = getCurrentMomentDate(timezone, timezoneMapping);

      let displayStartDateTime = eventDisplayStartDateTime;
      let displayEndDateTime = eventDisplayEndDateTime;

      if (!eventDisplayStartDateTime) {
        const eventDateTime = moment(eventDateTimeValue, DATE_TIME_FORMAT);
        const displayStartDateTimeMoment = eventDateTime.subtract(startDateOffset, 'days');
        displayStartDateTime = displayStartDateTimeMoment.format(DATE_TIME_FORMAT);
        element.display_start_date_time = displayStartDateTime;
      }

      if (!eventDisplayEndDateTime) {
        const eventEndDateTime = moment(eventEndDateTimeValue, DATE_TIME_FORMAT);
        const displayEndDateTimeMoment = eventEndDateTime.add(endDateOffset, 'days');
        displayEndDateTime = displayEndDateTimeMoment.format(DATE_TIME_FORMAT);
        element.display_end_date_time = displayEndDateTime;
      }

      const parsedStartDate = moment(displayStartDateTime, DATE_TIME_FORMAT);
      const parsedEndDate = moment(displayEndDateTime, DATE_TIME_FORMAT);
      // add the check for display_as_past_event_id
      if (displayAsPastEventId || (!displayAsPastEventId && (parsedCurrentDate.isSameOrBefore(parsedEndDate)
        && parsedCurrentDate.isSameOrAfter(parsedStartDate)))) {
        parsedEventsData.push(element);
      }
    }
  });

  return parsedEventsData;
};

const isTeaserEvent = (teaser, timezone, timezoneMapping) => {
  if (!teaser || (Object.keys(teaser).length === 0)) return false;
  const requiredKeys = ['text', 'start_date_time', 'end_date_time'];
  const isValidTeaser = requiredKeys
    .every((i) => Object.prototype.hasOwnProperty.call(teaser, i) && teaser[i]);

  if (!isValidTeaser) return false;
  const currentDate = getCurrentMomentDate(timezone, timezoneMapping);
  const parsedStartDate = moment(teaser.start_date_time, DATE_TIME_FORMAT);
  const parsedEndDate = moment(teaser.end_date_time, DATE_TIME_FORMAT);
  if (parsedStartDate.isValid && parsedEndDate.isValid) {
    if (currentDate.isSameOrBefore(parsedEndDate)
      && currentDate.isSameOrAfter(parsedStartDate)) return true;
  }
  return false;
};

const generateVenueEvents = async (eventsData, selectedPartySize,
  selectedDate, selectedCoreTime,
  locationBySlug, timezoneMapping, showAll, errorMessages) => {
  let venueIdsStr = '';
  const eventsDataLen = eventsData.length;

  if (eventsDataLen === 0) return [];
  let venueDetailsArr = [];
  if (!showAll) {
    eventsData.forEach((eventsDataItem, index) => {
      if (eventsDataLen !== index + 1) venueIdsStr += `${eventsDataItem.venue_id},`;
      else venueIdsStr += eventsDataItem.venue_id;
    });

    if (venueIdsStr === '') return [];
    venueDetailsArr = await fetchVenueDetails(venueIdsStr);
    if (!venueDetailsArr || venueDetailsArr.length === 0) return [];
  }

  const venueEventsArr = await Promise.all(eventsData.map(async (eventsDataItem) => {
    if (eventsDataItem.error) return eventsDataItem;
    const {
      venue_id: venueId,
      event_date_time: eventDateTime,
      event_end_date_time: eventEndDateTime,
      formatted_display_date: formattedDisplayDate,
      image_preview_text: imagePreviewText,
      event_name: hardcodedEventName,
    } = eventsDataItem;

    let updatedEventsDataItem = {};
    let venueDetails = {};

    if (!showAll) {
      venueDetails = venueDetailsArr.find((ele) => ele.id.resy
        === Number(venueId));
      if (!venueDetails) return {};
    } else {
      venueDetailsArr = await fetchVenueDetails(venueId);
      if (!venueDetailsArr || venueDetailsArr.length === 0) {
        const errObj = {
          error_source: ERROR_SOURCE.API,
          error_type: ERROR_TYPE.APIERROR,
          error_message: errorMessages?.error_venue_api_response_message,
        };
        eventsDataItem.error = errObj;
        return eventsDataItem;
      }
      [venueDetails] = venueDetailsArr;
    }

    const {
      name, responsive_images: responsiveImages, location: { url_slug: location }, url_slug: urlSlug,
    } = venueDetails;

    const query = {
      date: (eventsDataItem?.venue_date) ? eventsDataItem?.venue_date : selectedDate,
      seats: selectedPartySize,
      time: selectedCoreTime,
    };

    const getEventHref = () => `cities/${location}/venues/${urlSlug}?${parameterize(query)}`;

    const momentEventDate = moment(eventDateTime, DATE_TIME_FORMAT);
    const momentEventEndDate = moment(eventEndDateTime, DATE_TIME_FORMAT);

    const noOfDays = momentEventEndDate.diff(momentEventDate, 'days');

    let calculatedDisplayDate = formattedDisplayDate;
    if (!formattedDisplayDate || formattedDisplayDate.length === 0) {
      if (momentEventDate.isSame(momentEventEndDate)) {
        calculatedDisplayDate = `${momentEventDate.format('llll')}`;
      } else if (noOfDays === 0) {
        const endTime = eventEndDateTime.split(' ')[1];
        const endTimeFormatted = (endTime) ? ` - ${moment(endTime, 'HH:mm').format('LT')}` : '';
        calculatedDisplayDate = `${momentEventDate.format('llll')}${endTimeFormatted}`;
      } else {
        calculatedDisplayDate = `${momentEventDate.format('llll')} - ${momentEventEndDate.format('llll')}`;
      }
    }

    let calculatedPreviewData = imagePreviewText;
    if (imagePreviewText && (Object.keys(imagePreviewText).length > 0
      && Object.values(imagePreviewText).some((value) => value.trim() === ''))) {
      const calculatedDisplayDate1 = `${momentEventDate.format('MMM D')}`;
      const calculatedDisplayDate2 = `${momentEventEndDate.format('MMM D')}`;

      const splitDateArr1 = calculatedDisplayDate1.split(' ');
      const splitDateArr2 = calculatedDisplayDate2.split(' ');

      if (splitDateArr1.length === 2 && splitDateArr2.length === 2) {
        const month1 = splitDateArr1[0];
        const month2 = splitDateArr2[0];

        const date1 = splitDateArr1[1];
        const date2 = splitDateArr2[1];

        // same date
        if (noOfDays === 0) {
          calculatedPreviewData = {
            line_1: month1,
            line_2: date1,
          };
        } else if (month1 === month2) { // same month
          calculatedPreviewData = {
            line_1: month1,
            line_2: `${date1} - ${date2}`,
          };
        } else if (month1 !== month2 && noOfDays > 0) { // different month and dates
          calculatedPreviewData = {
            line_1: calculatedDisplayDate1,
            line_2: '-',
            line_3: calculatedDisplayDate2,
          };
        }
      }
    }
    const timezoneName = getEventCityTimeZone(location, locationBySlug);
    updatedEventsDataItem = {
      ...eventsDataItem,
      event_name: hardcodedEventName || name,
      event_date_time: eventDateTime,
      event_date_time_est: getEventDateTimeInEST(location, locationBySlug, eventDateTime, DATE_TIME_FORMAT, timezoneMapping),
      responsive_images: responsiveImages,
      event_url: getEventHref(),
      locations_text: locationBySlug[location]?.name,
      location,
      timezone: getEventCityTimeZone(location, locationBySlug),
      venue_slug: urlSlug,
      formatted_display_date: calculatedDisplayDate,
      image_preview_text: calculatedPreviewData,
      is_teaser: isTeaserEvent(eventsDataItem.teaser, timezoneName, timezoneMapping),
    };
    return updatedEventsDataItem;
  }));

  return venueEventsArr;
};

const placeNotify = async (eventData, seats) => {
  const {
    hasNotify,
    notifyDate,
    notifyEndTime,
    notifyStartTime,
    serviceTypeId,
    venueId,
  } = eventData.notify;

  if (hasNotify) {
    await deleteEventNotify({
      venue_id: venueId,
      day: notifyDate,
      num_seats: seats,
      service_type_id: serviceTypeId,
    });
  } else if (!hasNotify) {
    await setEventNotify({
      venue_id: venueId,
      day: notifyDate,
      time_preferred_start: notifyStartTime,
      time_preferred_end: notifyEndTime,
      num_seats: seats,
      service_type_id: serviceTypeId,
    });
  }

  // eslint-disable-next-line camelcase
  const { event_slug, location, venue_slug } = eventData;
  const eventDetails = await fetchEventDetails({ event_slug, location, venue_slug }, seats);
  return eventDetails;
};

const getMostAvailable = (ticketTypes, eventMaxPartySize) => {
  const mostAvailable = ticketTypes.reduce((acc, curr) => (curr.availableQuantity > acc
    ? curr.availableQuantity
    : acc), 0);

  if (mostAvailable > eventMaxPartySize) return eventMaxPartySize;

  return mostAvailable;
};

const isShowNotify = (event, selectedPartySize) => {
  const { packages, event_type: type, max_party_size: maxPartySize } = event;

  const mapResult = packages?.map((curr) => {
    let availableQuantity = curr.slots.length;

    if (eventTypeMap[curr.event_type] === GENERAL_ADMISSION && !curr.sold_out) {
      availableQuantity = (curr.slots.length > 0) ? curr.slots[0].quantity.remaining : 0;
    }

    return {
      ...curr,
      availableQuantity,
    };
  });

  const mostAvailable = getMostAvailable(mapResult, maxPartySize);
  const showNotify = type === SEATED ? !mostAvailable : selectedPartySize > mostAvailable;

  return showNotify;
};

const updateResyEventItem = (eventsDataItem, eventDetails,
  locationBySlug, timezoneMapping, selectedPartySize, selectedDate, selectedCoreTime) => {
  const query = {
    date: selectedDate,
    seats: selectedPartySize,
    time: selectedCoreTime,
  };
  let updatedEventsDataItem = {};
  const {
    location,
    event_name: hardcodedEventName,
    venue_slug: venueSlug,
    event_slug: eventSlug,
    teaser,
  } = eventsDataItem;
  const getEventHref = () => `cities/${location}/venues/${venueSlug}/events/${eventSlug}?${parameterize(query)}`;

  const {
    name,
    date,
    time,
    responsive_images: images,
    end_time: endTime,
    event_type: eventType,
    image_preview: imagePreview,
    notify_active: notifyActive,
    notify_options: notifyOptions,
    service_type_id: serviceTypeId,
    venue: { id: venueId, name: venueName },
    packages,
  } = eventDetails;

  const getVenueHref = () => `cities/${location}/venues/${venueSlug}?${parameterize(query)}`;
  const eventDateTime = moment(`${date} ${time}`, DATE_TIME_FORMAT_EVENT);
  const eventDateTimeFormatted = eventDateTime.format(DATE_TIME_FORMAT);

  const eventEndDateTime = moment(`${date} ${endTime}`, DATE_TIME_FORMAT_EVENT);
  const eventEndDateTimeFormatted = eventEndDateTime.format(DATE_TIME_FORMAT);

  const timezoneName = getEventCityTimeZone(location, locationBySlug);
  moment.tz.setDefault(timezoneName);
  const now = moment();
  const options = notifyOptions && notifyOptions[0] ? notifyOptions[0] : {};

  const notifyStart = moment(options.min_request_datetime, DATE_TIME_FORMAT_EVENT);
  const notifyDate = notifyStart.format(DATE_FORMAT);
  const notifyStartTime = notifyStart.format(TIME_FORMAT);
  const notifyEnd = moment(options.max_request_datetime, DATE_TIME_FORMAT_EVENT);
  const notifyEndTime = notifyEnd.format(TIME_FORMAT);

  const packageVal = packages?.find((curr) => (curr.sold_out === false));

  // include past events
  const parsedCurrentDate = getCurrentMomentDate(timezoneName, timezoneMapping);
  const parsedEndDate = moment(eventEndDateTimeFormatted, DATE_TIME_FORMAT);
  // ends

  updatedEventsDataItem = {
    ...eventsDataItem,
    event_name: hardcodedEventName || name,
    event_date: date,
    event_time: time,
    event_date_time: eventDateTimeFormatted,
    event_date_time_est: getEventDateTimeInEST(location, locationBySlug, `${date} ${time}`, DATE_TIME_FORMAT_EVENT, timezoneMapping),
    event_end_date_time: eventEndDateTimeFormatted,
    responsive_images: images,
    image_preview: imagePreview,
    event_url: getEventHref(),
    event_end_time: endTime,
    event_type: eventType,
    locations_text: locationBySlug[location]?.name,
    timezone: timezoneName,
    showNotify: isShowNotify(eventDetails, selectedPartySize) && !(now.diff(notifyEnd) > 0),
    notify: {
      hasNotify: !!notifyActive,
      notifyDate,
      notifyEndTime,
      notifyStartTime,
      serviceTypeId,
      venueId,
    },
    venueName,
    venue_url: getVenueHref(),
    event_id: Math.floor(Math.random() * 101),
    event_source: EVENT_SOURCE.RESY_API,
    sold_out: !(packageVal && (Object.keys(packageVal).length > 1)),
    is_teaser: isTeaserEvent(teaser, timezoneName, timezoneMapping),
    teaser,
    ...(parsedCurrentDate.isSameOrAfter(parsedEndDate) && { passed_event: true }),
  };
  return updatedEventsDataItem;
};
const generateResyEvents = async (eventsData, selectedPartySize, selectedDate, selectedCoreTime,
  locationBySlug, timezoneMapping, showAll, errorMessages) => {
  const resyEventsArr = await Promise.all(eventsData.map(async (eventsDataItem) => {
    if (eventsDataItem.error) return eventsDataItem;
    const eventDetails = await fetchEventDetails(eventsDataItem, selectedPartySize);

    if (!eventDetails && showAll) {
      const errObj = {
        error_source: ERROR_SOURCE.API,
        error_type: ERROR_TYPE.APIERROR,
        error_message: errorMessages?.error_resy_api_response_message,
      };

      eventsDataItem.error = errObj;
      return eventsDataItem;
    } if (!eventDetails) return null;

    return updateResyEventItem(eventsDataItem,
      eventDetails, locationBySlug, timezoneMapping, selectedPartySize, selectedDate, selectedCoreTime, showAll, errorMessages);
  }));
  return resyEventsArr;
};

const updatedEventsData = async (eventsData, eventConfig, selectedPartySize, selectedDate,
  selectedCoreTime, locationBySlug, showAll) => {
  if (!eventsData) return [];

  const timezoneMapping = eventConfig?.timezone_mappings;
  const errorMessages = eventConfig?.show_all_errors;
  if (!errorMessages) showAll = false;
  /* HARDCODED EVENTS */
  const query = {
    date: selectedDate,
    seats: selectedPartySize,
    time: selectedCoreTime,
  };
  const hardcodedEvents = eventsData?.HARDCODED_EVENTS;
  let validatedHardcodedEvents = null;
  let modifiedHardcodedEvents = [];
  if (hardcodedEvents) {
    validatedHardcodedEvents = validateJSONHardcodedEvents(hardcodedEvents, showAll, errorMessages);
    if (!validatedHardcodedEvents) validatedHardcodedEvents = [];
    modifiedHardcodedEvents = validatedHardcodedEvents
      .map((obj) => {
        const timezoneName = getEventCityTimeZone(obj.location, locationBySlug);
        return ({
          ...obj,
          event_id: Math.floor(Math.random() * 101),
          event_source: EVENT_SOURCE.HARDCODED,
          event_url: obj.event_url ? `${obj.event_url}?${parameterize(query)}` : '',
          ...(!obj.error
          && { event_date_time_est: getEventDateTimeInEST(obj.location, locationBySlug, obj.event_date_time, DATE_TIME_FORMAT, timezoneMapping) }),
          ...(!obj.error
          && { timezone: timezoneName }),
          is_teaser: isTeaserEvent(obj.teaser, timezoneName, timezoneMapping),
        });
      });
  }

  /* RESY EVENTS */
  const resyEvents = eventsData?.RESY_EVENTS;
  let validatedAPIResyEvents = [];
  if (resyEvents) {
    const modifiedResyEvents = resyEvents
      .map((obj) => ({
        ...obj,
        event_id: Math.floor(Math.random() * 101),
        event_source: EVENT_SOURCE.RESY_API,
      }));
    validatedAPIResyEvents = validateJSONResyEvents(modifiedResyEvents, showAll, errorMessages);
    if (!validatedAPIResyEvents) return [];

    const generatedResyEvents = await generateResyEvents(validatedAPIResyEvents, selectedPartySize, selectedDate, selectedCoreTime, locationBySlug, timezoneMapping, showAll, errorMessages);
    validatedAPIResyEvents = validateAPIResyEvents(generatedResyEvents, eventConfig, timezoneMapping, showAll, errorMessages);
  }

  /* RESTAURANT or VENUE EVENTS */
  let venueEvents = eventsData?.RESTAURANT_EVENTS;
  let validatedAPIVenueEvents = [];
  if (venueEvents) {
    const modifiedVenueEvents = venueEvents
      .map((obj) => ({
        ...obj,
        event_id: Math.floor(Math.random() * 101),
        event_source: EVENT_SOURCE.VENUE_API,
      }));

    validatedAPIVenueEvents = validateJSONVenueEvents(modifiedVenueEvents, showAll, errorMessages);
    if (!validatedAPIVenueEvents) return [];
    if (!showAll) venueEvents = parseValidVenueEvents(validatedAPIVenueEvents, eventConfig, timezoneMapping);
    else venueEvents = validatedAPIVenueEvents;
    const generatedVenueEvents = await generateVenueEvents(venueEvents, selectedPartySize, selectedDate, selectedCoreTime, locationBySlug, timezoneMapping, showAll, errorMessages);
    validatedAPIVenueEvents = validateAPIVenueEvents(generatedVenueEvents, eventConfig, timezoneMapping, showAll, errorMessages);
  }

  let finalHardcodedAndResyEvents = [];
  if (!showAll)finalHardcodedAndResyEvents = [...modifiedHardcodedEvents, ...validatedAPIResyEvents];
  else finalHardcodedAndResyEvents = [...modifiedHardcodedEvents, ...validatedAPIResyEvents, ...validatedAPIVenueEvents];

  const parsedEventsData = [];
  const parsedEventsErrData = [];
  finalHardcodedAndResyEvents.forEach((element) => {
    if (!element.error) {
      const parsedCurrentDate = getCurrentMomentDate(element.timezone, timezoneMapping);
      const { display_start_date_time: displayStartDateTime, display_end_date_time: displayEndDateTime, display_as_past_event_id: displayAsPastEventId } = element;
      const parsedStartDate = moment(displayStartDateTime, DATE_TIME_FORMAT);
      const parsedEndDate = moment(displayEndDateTime, DATE_TIME_FORMAT);
      // add the check for display_as_past_event_id
      if (!displayAsPastEventId) {
        if (parsedCurrentDate.isSameOrBefore(parsedEndDate)
          && parsedCurrentDate.isSameOrAfter(parsedStartDate)) {
          parsedEventsData.push(element);
        } else if (showAll) {
          const errorObj = {
            date_info_message: errorMessages?.error_date_message,
          };
          element.error = errorObj;
          parsedEventsErrData.push(element);
        }
      } else {
        parsedEventsData.push(element);
      }
    } else if (showAll) {
      parsedEventsErrData.push(element);
    }
  });

  if (!showAll) {
    validatedAPIVenueEvents.forEach((element) => {
      parsedEventsData.push(element);
    });
  }

  // sort the events chronologically
  const dateOrderedEvents = parsedEventsData?.map((event) => {
    const { event_end_date_time: eventEndDateTime } = event;
    const parsedCurrentDate = getCurrentMomentDate(event.timezone, timezoneMapping);
    const parsedEndDate = moment(eventEndDateTime, DATE_TIME_FORMAT);
    return {
      ...event,
      ...(parsedCurrentDate.isSameOrAfter(parsedEndDate) && { passed_event: true }),
    };
  })
    .sort((a, b) => (moment(a.event_date_time_est, DATE_TIME_FORMAT).diff(moment(b.event_date_time_est, DATE_TIME_FORMAT))));

  const allEvents = [...dateOrderedEvents, ...parsedEventsErrData];
  return allEvents;
};

const getTeaser = (teaser, timezone, timezoneMapping) => {
  if (Object.keys(teaser).length === 0) return null;
  const requiredKeys = ['text', 'start_date_time', 'end_date_time'];
  const isValidTeaser = requiredKeys
    .every((i) => Object.prototype.hasOwnProperty.call(teaser, i) && teaser[i]);

  if (!isValidTeaser) return null;
  const currentDate = getCurrentMomentDate(timezone, timezoneMapping);
  const parsedStartDate = moment(teaser.start_date_time, DATE_TIME_FORMAT);
  const parsedEndDate = moment(teaser.end_date_time, DATE_TIME_FORMAT);
  if (parsedStartDate.isValid && parsedEndDate.isValid) {
    if (currentDate.isSameOrBefore(parsedEndDate)
      && currentDate.isSameOrAfter(parsedStartDate)) return teaser.text;
  }
  return null;
};

const validateEventsSectionData = (eventsConfig) => {
  let isValid = true;
  const errorMessage = '[AmexExperiential] JSON VALIDATION - events section fieldName is a Mandatory field in ExperientialData JSON file';

  if (!eventsConfig?.display_start_date_offset) {
    isValid = false;
    consoleError(errorMessage, 'display_start_date_offset');
  } else if (Number.isNaN(Number(eventsConfig?.display_start_date_offset))) {
    isValid = false;
    Logger.error('[AmexExperiential] JSON VALIDATION - events section display_start_date_offset should be a number field in ExperientialData JSON file');
  }

  if (!eventsConfig?.display_end_date_offset) {
    isValid = false;
    consoleError(errorMessage, 'display_end_date_offset');
  } else if (Number.isNaN(Number(eventsConfig?.display_end_date_offset))) {
    isValid = false;
    Logger.error('[AmexExperiential] JSON VALIDATION - events section display_end_date_offset should be a number field in ExperientialData JSON file');
  }

  if (!eventsConfig?.cta_label
    || eventsConfig?.cta_label === '') {
    isValid = false;
    consoleError(errorMessage, 'cta_label');
  }
  if (!eventsConfig?.timezone_mappings
    || Object.keys(eventsConfig?.timezone_mappings).length === 0) {
    isValid = false;
    consoleError(errorMessage, 'timezone_mappings');
  }
  if (!eventsConfig?.sold_out_message
    || eventsConfig?.sold_out_message === '') {
    isValid = false;
    consoleError(errorMessage, 'sold_out_message');
  }
  return isValid;
};

const updateAsNewEvents = (eventItem, timezoneMappings, displayDateOffset) => {
  if (!eventItem.event_upload_date) {
    eventItem.isNewEvent = false;
    return eventItem;
  }
  const eventUploadDate = moment(eventItem.event_upload_date, DATE_FORMAT);
  const uploadDateWithOffsetMoment = eventUploadDate.add(displayDateOffset, 'days');

  if (!eventItem.passed_event && eventItem.event_upload_date && getCurrentMomentDate(eventItem.timezone, timezoneMappings).isBefore(uploadDateWithOffsetMoment)) {
    eventItem.isNewEvent = true;
  }
  return eventItem;
};

const findNewEvents = (contentData, updatedEvents) => {
  const {
    timezone_mappings: timezoneMappings,
  } = contentData?.events;

  const {
    no_of_events_to_display: noOfEventsToDisplay,
    display_date_offset: displayDateOffset,
  } = contentData?.newevents;

  // updating to new events starts
  updatedEvents = updatedEvents.map((item) => updateAsNewEvents(item, timezoneMappings, displayDateOffset));

  const filteredSortedNewEvents = updatedEvents.filter((item) => item != null && item.isNewEvent)?.sort((a, b) => (moment(a.event_upload_date, DATE_FORMAT).diff(moment(b.event_upload_date, DATE_FORMAT))));
  return filteredSortedNewEvents?.slice(0, noOfEventsToDisplay);
};

/**  STARTS */
const getHardcodedEventImageByRatio = (images, matchMedia) => {
  const {
    '16:9': image16x9,
    '4:3': image4x3,
  } = images;

  const isDesktop = matchMedia.md;
  if (isDesktop) return image4x3 || image16x9;
  return image16x9 || image4x3;
};

/** Ends */
export {
  updatedEventsData, handleTaggingWithProps,
  getTeaser, validateEventsSectionData, placeNotify, updateResyEventItem,
  updateWithCommonFields, getCurrentMomentDate, fetchEventDetails,
  getHardcodedEventImageByRatio, findNewEvents,
};
